import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { Editor } from '@tiptap/core';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Search, ChevronUp, ChevronDown, X, MoreVertical } from 'lucide-react';
import { findAllTextPositions, findScrollContainer } from '../utils/editor-utils';
import { clearSearchMatchHighlights, setSearchMatchHighlights } from '@narrativebench/editor-core';
import { useLocale } from '../contexts/LocaleContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

interface SearchBoxProps {
  editor: Editor | null;
  isFocusMode?: boolean;
  isMobile?: boolean;
  editorScrollContainerRef?: React.RefObject<HTMLDivElement>;
  initialReplaceMode?: boolean;
  initialSearchText?: string;
}

export const SearchBox: React.FC<SearchBoxProps & { onClose?: () => void; isVisible?: boolean }> = ({
  editor,
  isFocusMode = false,
  isMobile = false,
  editorScrollContainerRef,
  onClose,
  isVisible = true,
  initialReplaceMode = false,
  initialSearchText = '',
}) => {
  const { t } = useLocale();
  const [searchText, setSearchText] = useState(initialSearchText);
  const [replaceText, setReplaceText] = useState('');
  const [isReplaceMode, setIsReplaceMode] = useState(initialReplaceMode);
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const [positions, setPositions] = useState<Array<{ from: number; to: number }>>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const previousVisibleRef = useRef<boolean>(false);
  const previousReplaceModeRef = useRef<boolean>(initialReplaceMode);
  const shouldFocusReplaceRef = useRef<boolean>(false);

  useEffect(() => {
    if (isVisible && initialSearchText) {
      setSearchText(initialSearchText);
    }
  }, [initialSearchText, isVisible]);

  useEffect(() => {
    if (isVisible && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
        if (initialSearchText) {
          inputRef.current?.select();
        }
      }, 0);
    }
    previousVisibleRef.current = isVisible;
  }, [isVisible, initialSearchText]);

  useEffect(() => {
    if (initialReplaceMode) {
      setIsReplaceMode(true);
    }
  }, [initialReplaceMode]);

  useEffect(() => {
    const wasAlreadyVisible = previousVisibleRef.current;
    const wasAlreadyInReplaceMode = previousReplaceModeRef.current;
    if (isReplaceMode && replaceInputRef.current && isVisible) {
      const shouldFocus = shouldFocusReplaceRef.current || (!wasAlreadyInReplaceMode && wasAlreadyVisible);
      if (shouldFocus) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              if (replaceInputRef.current) {
                replaceInputRef.current.focus();
                if (document.activeElement === replaceInputRef.current) {
                  shouldFocusReplaceRef.current = false;
                }
              }
            }, 300);
          });
        });
      }
    }
    previousReplaceModeRef.current = isReplaceMode;
  }, [isReplaceMode, isVisible]);

  const clearSearchDecorations = useCallback(() => {
    if (!editor) return;
    const tr = editor.state.tr;
    clearSearchMatchHighlights(tr);
    editor.view.dispatch(tr);
  }, [editor]);

  const applySearchDecorations = useCallback((
    matches: Array<{ from: number; to: number; plainFrom?: number; plainTo?: number }>,
    activeIndex: number,
  ) => {
    if (!editor) return;
    const tr = editor.state.tr;
    setSearchMatchHighlights(
      tr,
      matches.flatMap((match) => {
        if (match.plainFrom == null || match.plainTo == null) {
          return [];
        }
        return [{
          from: match.from,
          to: match.to,
          plainFrom: match.plainFrom,
          plainTo: match.plainTo,
        }];
      }),
      activeIndex,
    );
    editor.view.dispatch(tr);
  }, [editor]);

  const scrollToPosition = useCallback((position: { from: number; to: number }, keepInputFocused = false) => {
    if (!editor) return;

    editor.commands.setTextSelection({ from: position.from, to: position.to });

    const scrollContainer = editorScrollContainerRef?.current || findScrollContainer(editor.view.dom);
    if (scrollContainer) {
      const editorView = editor.view;
      const coords = editorView.coordsAtPos(position.from);
      const scrollContainerRect = scrollContainer.getBoundingClientRect();
      const scrollTop = scrollContainer.scrollTop;
      const scrollLeft = scrollContainer.scrollLeft;

      const relativeTop = coords.top - scrollContainerRect.top + scrollTop;
      const relativeLeft = coords.left - scrollContainerRect.left + scrollLeft;

      scrollContainer.scrollTo({
        top: relativeTop - scrollContainerRect.height / 2,
        left: relativeLeft,
        behavior: 'smooth',
      });
    }

    if (keepInputFocused && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [editor, editorScrollContainerRef]);

  useEffect(() => {
    if (!editor || searchText === '') {
      setPositions([]);
      setCurrentIndex(-1);
      clearSearchDecorations();
      return;
    }

    const debounceMs = 150;
    const timeoutId = setTimeout(() => {
      const allPositions = findAllTextPositions(editor, searchText);
      setPositions(allPositions);
      if (allPositions.length > 0) {
        const { state } = editor;
        const caretPosition = state.selection.from;
        const nextMatchIndex = allPositions.findIndex((pos) => pos.from >= caretPosition);
        const initialIndex = nextMatchIndex !== -1 ? nextMatchIndex : 0;
        setCurrentIndex(initialIndex);
        applySearchDecorations(allPositions, initialIndex);
        scrollToPosition(allPositions[initialIndex], true);
      } else {
        setCurrentIndex(-1);
        clearSearchDecorations();
      }
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [searchText, editor, clearSearchDecorations, applySearchDecorations, scrollToPosition]);

  useEffect(() => {
    return () => {
      clearSearchDecorations();
    };
  }, [clearSearchDecorations]);

  useEffect(() => {
    if (!onClose) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        const isDropdownMenu = target.closest('[data-radix-popper-content-wrapper]') ||
                              target.closest('[role="menu"]') ||
                              target.closest('[data-radix-dropdown-menu-content]');
        if (!target.closest('button[aria-label*="search"]') &&
            !target.closest('button[title*="search"]') &&
            !isDropdownMenu) {
          clearSearchDecorations();
          setSearchText('');
          onClose();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, clearSearchDecorations]);

  const handleNext = () => {
    if (positions.length === 0) return;
    const nextIndex = (currentIndex + 1) % positions.length;
    setCurrentIndex(nextIndex);
    applySearchDecorations(positions, nextIndex);
    scrollToPosition(positions[nextIndex], true);
  };

  const handlePrevious = () => {
    if (positions.length === 0) return;
    const prevIndex = currentIndex <= 0 ? positions.length - 1 : currentIndex - 1;
    setCurrentIndex(prevIndex);
    applySearchDecorations(positions, prevIndex);
    scrollToPosition(positions[prevIndex], true);
  };

  const handleReplace = useCallback(() => {
    if (!editor || searchText === '' || currentIndex < 0 || positions.length === 0) return;

    const position = positions[currentIndex];

    if (replaceText === '') {
      editor.chain()
        .setTextSelection({ from: position.from, to: position.to })
        .deleteSelection()
        .run();
    } else {
      const { state } = editor;
      const tr = state.tr;
      tr.replaceWith(position.from, position.to, state.schema.text(replaceText));
      editor.view.dispatch(tr);
    }

    setTimeout(() => {
      if (!editor) return;
      const newPositions = findAllTextPositions(editor, searchText);
      setPositions(newPositions);

      if (newPositions.length > 0) {
        const nextIndex = Math.min(currentIndex, newPositions.length - 1);
        setCurrentIndex(nextIndex);
        applySearchDecorations(newPositions, nextIndex);
        scrollToPosition(newPositions[nextIndex], false);
      } else {
        setCurrentIndex(-1);
        clearSearchDecorations();
      }
      if (replaceInputRef.current) {
        replaceInputRef.current.focus();
      }
    }, 0);
  }, [editor, searchText, replaceText, currentIndex, positions, scrollToPosition, clearSearchDecorations, applySearchDecorations]);

  const hasCurrentMatch = () => {
    return currentIndex >= 0 && positions.length > 0 && searchText !== '';
  };

  const handleSkip = () => {
    handleNext();
  };

  const handleReplaceAll = useCallback(() => {
    if (!editor || searchText === '' || positions.length === 0) return;

    if (replaceText === '') {
      for (let i = positions.length - 1; i >= 0; i--) {
        const position = positions[i];
        editor.chain()
          .setTextSelection({ from: position.from, to: position.to })
          .deleteSelection()
          .run();
      }
    } else {
      const { state } = editor;
      const tr = state.tr;
      for (let i = positions.length - 1; i >= 0; i--) {
        const position = positions[i];
        tr.replaceWith(position.from, position.to, state.schema.text(replaceText));
      }
      editor.view.dispatch(tr);
    }

    setTimeout(() => {
      setPositions([]);
      setCurrentIndex(-1);
      clearSearchDecorations();
    }, 0);
  }, [editor, searchText, replaceText, positions, clearSearchDecorations]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        handlePrevious();
      } else {
        handleNext();
      }
      e.preventDefault();
    } else if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'R') {
      e.preventDefault();
      shouldFocusReplaceRef.current = true;
      setIsReplaceMode(true);
      setIsDropdownOpen(false);
    } else if (e.key === 'Escape') {
      if (currentIndex >= 0 && positions.length > 0 && editor) {
        const currentPosition = positions[currentIndex];
        clearSearchDecorations();
        editor.commands.setTextSelection({ from: currentPosition.from, to: currentPosition.to });
        editor.commands.focus();
      } else {
        clearSearchDecorations();
      }
      setSearchText('');
      setReplaceText('');
      setIsReplaceMode(false);
      if (onClose) {
        onClose();
      }
      inputRef.current?.blur();
    }
  };

  const handleReplaceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        handleReplaceAll();
      } else {
        if (hasCurrentMatch()) {
          handleReplace();
        }
      }
      e.preventDefault();
    } else if (e.key === 'Escape') {
      if (currentIndex >= 0 && positions.length > 0 && editor) {
        const currentPosition = positions[currentIndex];
        clearSearchDecorations();
        editor.commands.setTextSelection({ from: currentPosition.from, to: currentPosition.to });
        editor.commands.focus();
      } else {
        clearSearchDecorations();
      }
      setSearchText('');
      setReplaceText('');
      setIsReplaceMode(false);
      if (onClose) {
        onClose();
      }
      replaceInputRef.current?.blur();
    }
  };

  const totalCount = positions.length;
  const currentNumber = currentIndex >= 0 ? currentIndex + 1 : 0;

  if (!isVisible) {
    return null;
  }

  const topPosition = isFocusMode
    ? 'calc(1rem + 40px + 0.5rem)'
    : 'calc(64px + 1rem)';
  const rightPosition = isMobile ? '1.8rem' : '4rem';

  return (
    <div
      ref={containerRef}
      data-editor-overlay=""
      className="search-box bg-card text-card-foreground border border-border shadow-lg"
      style={{
        position: 'fixed',
        top: topPosition,
        right: rightPosition,
        zIndex: 50,
        borderRadius: '0.5rem',
        padding: '0.5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        minWidth: '300px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      {searchText && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.875rem', minWidth: '60px', paddingLeft: '0.5rem', paddingRight: '0.5rem' }} className="text-muted-foreground">
          <span>{currentNumber > 0 ? `${currentNumber} / ${totalCount}` : totalCount > 0 ? `0 / ${totalCount}` : '0'}</span>
        </div>
      )}
      <div style={{ position: 'relative', flex: 1 }}>
        <Search
          style={{
            position: 'absolute',
            left: '0.5rem',
            top: '50%',
            transform: 'translateY(-50%)',
            height: '1rem',
            width: '1rem',
            pointerEvents: 'none',
          }}
          className="text-muted-foreground"
        />
        <Input
          ref={inputRef}
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('search_in_document') || 'Search in document'}
          style={{ paddingLeft: '2rem' }}
        />
      </div>
      <button
        onClick={handlePrevious}
        disabled={!searchText || totalCount === 0}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '28px',
          height: '28px',
          border: 'none',
          backgroundColor: (!searchText || totalCount === 0) ? 'var(--muted)' : 'transparent',
          borderRadius: '0.25rem',
          cursor: (!searchText || totalCount === 0) ? 'not-allowed' : 'pointer',
          color: (!searchText || totalCount === 0) ? 'var(--muted-foreground)' : 'var(--foreground)',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          if (searchText && totalCount > 0) {
            e.currentTarget.style.backgroundColor = 'var(--muted)';
          }
        }}
        onMouseLeave={(e) => {
          if (searchText && totalCount > 0) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
        aria-label={t('previous_match') || 'Previous match'}
        title={t('previous_match') || 'Previous match'}
      >
        <ChevronUp className="h-4 w-4" />
      </button>
      <button
        onClick={handleNext}
        disabled={!searchText || totalCount === 0}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '28px',
          height: '28px',
          border: 'none',
          backgroundColor: (!searchText || totalCount === 0) ? 'var(--muted)' : 'transparent',
          borderRadius: '0.25rem',
          cursor: (!searchText || totalCount === 0) ? 'not-allowed' : 'pointer',
          color: (!searchText || totalCount === 0) ? 'var(--muted-foreground)' : 'var(--foreground)',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          if (searchText && totalCount > 0) {
            e.currentTarget.style.backgroundColor = 'var(--muted)';
          }
        }}
        onMouseLeave={(e) => {
          if (searchText && totalCount > 0) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
        aria-label={t('next_match') || 'Next match'}
        title={t('next_match') || 'Next match'}
      >
        <ChevronDown className="h-4 w-4" />
      </button>
      <DropdownMenu
        open={isDropdownOpen}
        onOpenChange={(open) => {
          setIsDropdownOpen(open);
          if (!open && shouldFocusReplaceRef.current && replaceInputRef.current) {
            setTimeout(() => {
              if (replaceInputRef.current) {
                replaceInputRef.current.focus();
                shouldFocusReplaceRef.current = false;
              }
            }, 100);
          }
        }}
      >
        <DropdownMenuTrigger asChild>
          <button
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              border: 'none',
              backgroundColor: 'transparent',
              borderRadius: '0.25rem',
              cursor: 'pointer',
              color: 'var(--muted-foreground)',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--muted)';
              e.currentTarget.style.color = 'var(--foreground)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--muted-foreground)';
            }}
            aria-label={t('search_options') || 'Search options'}
            title={t('search_options') || 'Search options'}
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => {
              shouldFocusReplaceRef.current = true;
              setIsReplaceMode(true);
              setIsDropdownOpen(false);
            }}
          >
            {t('search_and_replace') || 'Search and replace'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <button
        onClick={() => {
          clearSearchDecorations();
          setSearchText('');
          setReplaceText('');
          setIsReplaceMode(false);
          if (onClose) {
            onClose();
          }
          inputRef.current?.blur();
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '28px',
          height: '28px',
          border: 'none',
          backgroundColor: 'transparent',
          borderRadius: '0.25rem',
          cursor: 'pointer',
          color: '#6b7280',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#f3f4f6';
          e.currentTarget.style.color = '#111827';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = '#6b7280';
        }}
        aria-label={t('close_search') || 'Close search'}
        title={t('close_search') || 'Close search'}
      >
        <X className="h-4 w-4" />
      </button>
      </div>
      {isReplaceMode && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%' }}>
          <Input
            ref={replaceInputRef}
            type="text"
            value={replaceText}
            onChange={(e) => setReplaceText(e.target.value)}
            onKeyDown={handleReplaceKeyDown}
            placeholder={t('replace_with') || 'Replace with'}
            style={{ flex: 1 }}
          />
          <Button
            onClick={handleSkip}
            disabled={positions.length === 0}
            variant="outline"
            size="sm"
            aria-label={t('skip') || 'Skip'}
            title={t('skip') || 'Skip'}
          >
            {t('skip') || 'Skip'}
          </Button>
          <Button
            onClick={handleReplace}
            disabled={!hasCurrentMatch()}
            variant="default"
            size="sm"
            aria-label={t('replace') || 'Replace'}
            title={t('replace') || 'Replace'}
          >
            {t('replace') || 'Replace'}
          </Button>
          <Button
            onClick={handleReplaceAll}
            disabled={positions.length === 0}
            variant="outline"
            size="sm"
            aria-label={t('replace_all') || 'Replace all'}
            title={t('replace_all') || 'Replace all'}
          >
            {t('replace_all') || 'Replace all'}
          </Button>
        </div>
      )}
    </div>
  );
};
